import {
  CreateClientNotification,
  CreateNotification,
  IGetQuestion,
  INotificationFilter,
  INotificationResponse,
  INotificationListResponse,
} from '../interfaces/INotification';
import { RootState } from './../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  tagTypes: ['Notification'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    getNotifications: build.query<
      INotificationResponse,
      INotificationFilter
    >({
      transformResponse: (response: { data: INotificationResponse }) =>
        response.data,
      query: (params: INotificationFilter) => ({
        url: '/notifications/list',
        method: 'GET',
        params
      }),
      providesTags: () => ['Notification'],
    }),
    getNotificationsList: build.query<
      INotificationListResponse,
      { page?: number; limit?: number; aggregateType?: string; status?: string; filterId?: string }
    >({
      transformResponse: (response: { data: INotificationListResponse }) =>
        response.data,
      query: (params) => ({
        url: '/notifications/list',
        method: 'GET',
        params,
      }),
      providesTags: () => ['Notification'],
    }),
    getMainAgentsNotifications: build.query<
      INotificationResponse,
      {
        search?: string;
        page?: number;
        from_date?: string;
        to_date?: string;
        agent_ids?: number[] | undefined;
        clients?: number[] | undefined;
      }
    >({
      transformResponse: (response: { data: INotificationResponse }) =>
        response.data,
      query: ({ search, page, to_date, from_date, agent_ids, clients }) => ({
        url: '/agents/notifications',
        method: 'GET',
        params: {
          page,
          search,
          to_date,
          from_date,
          agent_ids: JSON.stringify(agent_ids),
          clients: JSON.stringify(clients),
        },
      }),
    }),
    getMainClientsNotifications: build.query<
      INotificationResponse,
      {
        search?: string;
        page?: number;
        from_date?: string;
        to_date?: string;
        agent_ids?: number[] | undefined;
        clients?: number[] | undefined;
      }
    >({
      transformResponse: (response: { data: INotificationResponse }) =>
        response.data,
      query: ({ search, page, to_date, from_date, agent_ids, clients }) => ({
        url: '/clients/notifications',
        method: 'GET',
        params: {
          page,
          search,
          to_date,
          from_date,
          agent_ids: JSON.stringify(agent_ids),
          clients: JSON.stringify(clients),
        },
      }),
    }),
    getMainNotifications: build.query<
      INotificationResponse,
      {
        search?: string;
        page?: number;
        from_date?: string;
        to_date?: string;
        agent_ids?: number[] | undefined;
        clients?: number[] | undefined;
      }
    >({
      transformResponse: (response: { data: INotificationResponse }) =>
        response.data,
      query: ({ search, page, to_date, from_date, agent_ids, clients }) => ({
        url: '/agents/notifications',
        method: 'GET',
        params: {
          page,
          search,
          to_date,
          from_date,
          agent_ids: JSON.stringify(agent_ids),
          clients: JSON.stringify(clients),
        },
      }),
    }),
    createNotification: build.mutation<
      INotificationResponse,
      CreateNotification
    >({
      query: (notification) => ({
        url: `/agents/notifications`,
        method: 'POST',
        body: notification,
      }),
    }),
    deleteNotification: build.mutation<any, string>({
      query: (id) => ({
        url: `/notifications/${id}`,
        method: 'DELETE',
      }),
    }),
    readAllNotifications: build.mutation<any, string>({
      query: () => ({
        url: `/notifications/read`,
        method: 'POST',
      }),
    }),
    createClientNotification: build.mutation<
      INotificationResponse,
      CreateClientNotification
    >({
      query: (notification) => ({
        url: `/clients/notifications`,
        method: 'POST',
        body: notification,
      }),
    }),
    getAgentNotifications: build.query<
      INotificationResponse,
      {
        search: string;
        page: number;
        id: string;
        from_date: string;
        to_date: string;
        type_message: string;
      }
    >({
      transformResponse: (response: { data: INotificationResponse }) =>
        response.data,
      query: ({ search, page, id, from_date, to_date, type_message }) => ({
        url: `/agents/${id}/notifications`,
        method: 'GET',
        params: {
          search,
          page,
          from_date,
          to_date,
          type_message,
        },
      }),
      providesTags: () => ['Notification'],
    }),
    createAgentNotification: build.mutation<
      INotificationResponse,
      { data: Omit<CreateNotification, 'title'>; agent_id: number }
    >({
      transformResponse: (response: { data: INotificationResponse }) =>
        response.data,
      query: ({ data, agent_id }) => ({
        url: `/agents/${agent_id}/notifications`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Notification'],
    }),
    getQuestion: build.query<IGetQuestion, { id: number }>({
      transformResponse: (response: { data: IGetQuestion }) => response.data,
      query: ({ id }) => ({
        url: `/api/question/${id}`,
        method: 'GET',
      }),
      providesTags: () => ['Notification'],
    }),
    postQuestion: build.mutation<any, { id: number; variable_id: number }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ id, variable_id }) => ({
        url: `/api/question/${id}`,
        method: 'POST',
        body: { variable_id },
      }),
      invalidatesTags: ['Notification'],
    }),
  }),
});

export const {
  useGetNotificationsListQuery,
  useLazyGetNotificationsListQuery,
} = notificationsApi;
